import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel } from '../components/SectionLabel';
import { Bloom } from '../components/Bloom';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
const reasons = [{
  title: 'You already pay for leads. This makes sure you don’t lose them.',
  body: 'Every call, text, and Yelp message gets answered, including the ones that come in mid-job.'
}, {
  title: 'One system, not fifty logins.',
  body: 'Leads, follow-up, invoices, and reviews live in one place you actually check.'
}, {
  title: 'One team, not five vendors pointing fingers.',
  body: 'You stop being the middleman between the ad guy, the site guy, and the review guy.'
}, {
  title: 'We build the hard parts with you: AI receptionist, workflows, all in your voice.',
  body: 'Set up on a call, in your words. Not templates handed over as homework.'
}, {
  title: 'Works the same solo or with a crew.',
  body: 'One truck or five, it runs the same and grows when you do.'
}, {
  title: 'Month to month. We earn it every month.',
  body: 'No contracts. If it stops being worth it, you leave.'
}];
export function Why() {
  return <>
      <SEOHead title="Why Viridia Analytics | One System, One Team, No Missed Leads | Phoenix" description="Why Phoenix home-service businesses switch to Viridia: one system instead of fifty logins, one team instead of five vendors, and no more missed leads." canonical="/why" data-id="element-3183" data-mp="37z7mui1jwl2v" />

      <PageHeader label="Why Viridia" title="Why Viridia Analytics" intro="Six reasons owners switch, all of them about the jobs you book, not about us." data-id="element-3184" data-mp="2k3bvxmeqggal" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-3185" data-mp="iaf8f03mzl9l">
        <Bloom size="md" className="left-0 top-[-6rem]" data-id="element-3186" data-mp="n8p7hlshf60h" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-3187" data-mp="n7e1dfbkdm67">
          <h2 className="sr-only" data-id="element-3188" data-mp="ltw8geexd9s8">Reasons to work with Viridia Analytics</h2>
          <ul className="grid grid-cols-1 gap-x-12 gap-y-10 md:grid-cols-2" data-id="element-3189" data-mp="gsqeyedfxihn">
            {reasons.map((r, i) => <motion.li key={r.title} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="border-t border-[#1c1c1c] pt-6" data-id="element-3190" data-mp="bux77gwhogf9" data-mpi={i}>
                <h3 className="text-lg font-semibold leading-snug text-white" data-id="element-3191" data-mp="usrenqhyyjvj">{r.title}</h3>
                <p className="mt-2.5 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-3192" data-mp="qrxpktssiz12">
                  {r.body}
                </p>
              </motion.li>)}
          </ul>

          <div className="mt-16 flex flex-col items-start gap-6 rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-7 sm:flex-row sm:items-center" data-id="element-3193" data-mp="1iivjg16w8po4">
            <div className="rounded-xl bg-white p-3" data-id="element-3194" data-mp="316wz6bx1jq8u">
              <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot SEO certified badge held by the Viridia Analytics team" className="h-14 w-auto object-contain" data-id="element-3195" data-mp="364f0ph8qb36t" />
            </div>
            <div className="rounded-xl bg-white p-3" data-id="element-3196" data-mp="3k4hd4ny9p4eo">
              <img src="https://cdn.magicpatterns.com/uploads/8HQjTNAJm388sm9my7xsCp/Scotsscommerce.png" alt="Viridia Analytics is a Scottsdale Area Chamber of Commerce member" className="h-14 w-auto object-contain" data-id="element-3197" data-mp="3k3y827tpuai2" />
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-neutral-400" data-id="element-3198" data-mp="1p6ws8bd899k">
              HubSpot SEO certified and a Scottsdale Area Chamber of Commerce member.
            </p>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-3199" data-mp="bmjwm295re2m">
        <Bloom size="md" className="right-0 top-[-6rem]" data-id="element-3200" data-mp="3u5ifjb10c4cu" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-3201" data-mp="3p90eal4vyo59">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45
        }} data-id="element-3202" data-mp="7rqvkltw8gv2">
            <SectionLabel data-id="element-3203" data-mp="3b7nxk7hwiiib">Who&rsquo;s behind it</SectionLabel>
            <h2 className="mt-8 max-w-3xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3204" data-mp="3baaeuyulgcdq">
              Built by people who&rsquo;ve worked both sides of it
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-3205" data-mp="3l4aiewa899kc">
              Viridia didn&rsquo;t come out of a marketing agency. It came from doing the work,
              then learning how to sell it.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#1c1c1c] md:grid-cols-2" data-id="element-3206" data-mp="vgjo08bb46kx">
            {[{
            title: 'In the field',
            body: 'Years on the tools: cabinet finishing and installation, shower remodels, pest control. Sixteen-hour shifts to hit a deadline, and the standard that a job only counts when it’s done right.'
          }, {
            title: 'Behind the campaigns',
            body: 'Then the other side: former Yelp reps who onboarded home-service owners and managed their ad campaigns directly. Over $2 million a month in spend, run for real, scalable growth.'
          }].map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.06
          }} className="bg-[#0d0d0d] p-8" data-id="element-3207" data-mp="1h0ddur299p0b" data-mpi={i}>
                <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-500" data-id="element-3208" data-mp="2jblm19jgygzb">
                  {c.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-neutral-400" data-id="element-3209" data-mp="2ac88a4obb68s">{c.body}</p>
              </motion.div>)}
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: 0.1
        }} className="mt-10 max-w-3xl border-l-2 border-emerald-500 pl-5 text-lg leading-relaxed text-neutral-300" data-id="element-3210" data-mp="25c2fuma7xfpn">
            Almost no one has done both. That&rsquo;s why the system is built around how a
            home-service business actually runs, not how it looks from the outside.
          </motion.p>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-3211" data-mp="1cny6nqsfuki7">
        <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-3212" data-mp="12krqfr1gmjn5" />
        <div className="mx-auto w-full max-w-3xl px-6 py-24 text-center lg:py-28" data-id="element-3213" data-mp="12mvse3fst8ks">
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3214" data-mp="17hrp3gvzck48">
            Worth fifteen minutes?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-3215" data-mp="1hibr4lrbpsni">
            We look at where your leads go today and tell you straight if this helps.
          </p>
          <div className="mt-9 flex justify-center" data-id="element-3216" data-mp="osv1fvv38puz">
            <CTAButton to="/contact" data-id="element-3217" data-mp="oteaso6oa5xe">Book a call</CTAButton>
          </div>
        </div>
      </section>
    </>;
}